import _ from 'lodash'
import Backbone from 'backbone'
import colorIcon from './color-icon'
import * as d3 from 'd3-selection'
import Tooltip from 'scoreboard/components/tooltip'
import enums from 'scoreboard/components/enums'
import i18n from 'scoreboard/components/i18n'
import Constants from './constants'
const { RICH_TEXT_COLOR_PREFIX } = Constants

const PERFORMANCE = 'performance'
const AUTO = 'auto'

var ColorPicker = Backbone.View.extend({
	className: 'color-picker select',
	tagName: 'button',
	attributes: {
		type: 'button'
	},

	events: {
		'click': 'toggleTooltip'
	},

	initialize: function (opt) {
		this._colors = opt.colors
		this.tooltipClass = opt.tooltipClass
		this.$el.data('view', this)
	},

	/**
	 * Changes the currently picked color
	 */
	color: function (newColor) {
		this._color = newColor
		this.$el.empty().append(colorIcon(newColor))
	},

	/**
	 * Changes the set of colors that appear in the tooltip
	 */
	colors: function (newColors) {
		this._colors = newColors
	},

	close: function () {
		this._removeTooltip()
	},

	toggleTooltip: function () {
		if (this.tooltip) {
			this._removeTooltip()
			return
		}

		this.tooltipContent = new ColorPickerTooltip({
			colors: this._colors,
			color: this._color
		})

		this.tooltip = new Tooltip({
			content: this.tooltipContent,
			attachTo: this.el,
			placement: 'below',
			arrow: 'center',
			extraClass: this.tooltipClass
		}).render()

		this.listenTo(this.tooltipContent, 'select', function (color) {
			if (color === this._color) {
				// Don't fire 'select' event unless color is actually changing
				return
			}

			this.color(color)
			this.close()
			this.trigger('select', color)
		})

		this.tooltip.on('remove', function() { delete this.tooltip }.bind(this))
	},

	_removeTooltip: function () {
		this.tooltip && this.tooltip.remove()
		delete this.tooltip
	},

	val: function() {
		return this._color
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)
		this._removeTooltip()
	}
})

var ColorPickerTooltip = Backbone.View.extend({
	className: 'color-picker-tooltip',

	events: {
		'click': '_onClick'
	},

	initialize: function (opt) {
		this._colors = opt.colors
		this.color = opt.color
	},

	render: function () {
		// Need to use d3 or the SVG click events won't work in IE
		var selected = this.color

		// this._colors is a 2D array of colors, Each inner array is a group of
		// colors that belongs in the same row
		let colors = this._colors.map(group => {
			return group.map(c => {
				// Each group should consist of objects with the colorName and
				// an optional svg icon name to override the default
				if (typeof c === 'string') {
					return { colorName: c, value: c }
				} else {
					return _.extend({
						value: c.colorName
					}, c)
				}
			})
		})

		var group = d3.select(this.el).selectAll('.color-group')
			.data(colors)

		var icon = group.enter()
			.append('div')
			.classed('color-group', true)
			.merge(group)
			.selectAll('.color-icon')
			.data(function (d, i) { return d })

		var iconEnter = icon.enter().append('svg')
			.attr('class', function (color) {
				return 'color-icon ' + color.colorName
			})

		iconEnter.append('title')
			.text(function(color) {
				// Account for rich text colors so enums are properly retrieved
				const rawColor = color.colorName
					.replace(RICH_TEXT_COLOR_PREFIX, '')
				return i18n.get(enums.scoreColor[rawColor])
			})

		iconEnter.append('use')
			.attr('xlink:href', function(color) {
				return color.svg || '#icon-circle-fill'
			})

		let onClick = (e, d) => {
			// don't want other tooltips to unintentionally close (like in rich text widgets)
			e.preventDefault()
			e.stopPropagation()

			this.color = d
			this.trigger('select', d.value)
			this.render()
		}

		iconEnter.merge(icon)
			.classed('is-selected', function (d) {
				if (!selected) {
					return false
				}

				return d.value === selected
			 })
			.on('click', onClick)
			// #26824 For some reason, the click event isn't consistently firing on
			// iOS. touchend is, though, so we'll just listen for that too
			// (the listener for the 'select' event will take care of swallowing
			// duplicate events)
			.on('touchend', onClick)

		return this
	},

	_onClick: function (e) {
		// If the user accidentally clicks on an empty area of the tooltip, clobber the event
		// so other tooltips don't unintentionally close (like in rich text widgets)
		e.stopPropagation()
	}
})

/**
 * Returns the two color options necessary to render the 'performance' option
 * in a ColorPicker. Note that it returns 2 icons, one of which will be hidden
 * by CSS depending on hover/selected state.
 *
 * @param {Boolean} grayscale false will return the multi-colored version of
 * the icon; true will return the grayscale version
 *
 * @param {String} [value='performance'] the value to use when the performance
 * option is selected. For example, allows 'auto' to be used instead, while
 * still using the performance icon
 */
function buildPerformanceOption (grayscale, value = PERFORMANCE) {
	let performance = grayscale ? 'performance-grayscale' : 'performance'
	return [
		{
			value,
			colorName: performance,
			svg: `#icon-${performance}-circle`
		},{
			value,
			colorName: performance + '-hover-selected',
			svg: `#icon-${performance}-circle`
		}
	]
}

const options = ColorPickerTooltip
_.extend(ColorPicker, {
	options,
	buildPerformanceOption,
	PERFORMANCE,
	AUTO
})

export default ColorPicker


ColorPicker.__filename__ = 'components/color-picker.js'
